import React, { useState, useEffect } from 'react';
import { useLeadModal } from '../context/LeadModalContext';
import { useCountry } from '../context/CountryContext';
import { useLocation } from 'react-router-dom';
import { apiPost } from '../services/api';
import CountryPhoneInput from './CountryPhoneInput';
import { countries } from '../utils/countries';

const COURSE_MAP = [
  { slug: '200-hour-yoga-teacher-training-rishikesh', title: '200 Hour Yoga Teacher Training', id: 1 },
  { slug: '300hr-yoga-ttc', title: '300 Hour Yoga Teacher Training', id: 2 },
  { slug: '500hr-yoga-ttc', title: '500 Hour Yoga Teacher Training', id: 3 },
  { slug: 'diet-and-nutrition-course-online', title: 'Diet and Nutrition Course', id: 4 },
  { slug: 'face-yoga-teacher-training-course-online', title: 'Face Yoga Teacher Training Course', id: 5 },
  { slug: '95-hour-kids-yoga-teacher-training', title: '95 Hour Kids Yoga Teacher Training', id: 6 },
  { slug: 'pcos-pcod-management-program', title: 'PCOS & PCOD Management Program', id: 7 }
];

const getCourseFromPath = (pathname) => {
  const clean = (pathname || '').split('#')[0].split('?')[0].toLowerCase();
  const match = COURSE_MAP.find(c => clean.includes(c.slug));
  if (match) return match;
  if (clean.includes('200-hour') || clean.includes('200hr')) return COURSE_MAP[0];
  if (clean.includes('300hr')) return COURSE_MAP[1];
  if (clean.includes('500hr')) return COURSE_MAP[2];
  if (clean.includes('nutrition') || clean.includes('diet')) return COURSE_MAP[3];
  if (clean.includes('face-yoga')) return COURSE_MAP[4];
  if (clean.includes('kids-yoga')) return COURSE_MAP[5];
  if (clean.includes('pcos')) return COURSE_MAP[6];
  return null;
};

const LeadModal = () => {
  const { isOpen, defaultProgram, courseData, closeModal } = useLeadModal();
  const { selectedCountry } = useCountry();
  const location = useLocation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+91',
    phone: '',
    country: 'India',
    countryIso: 'IN',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Close modal when route changes
  useEffect(() => {
    closeModal();
  }, [location.pathname]);

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        closeModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Detect course from props or route
  const detectedRouteCourse = getCourseFromPath(location.pathname);
  const currentCourseTitle = defaultProgram || courseData?.title || detectedRouteCourse?.title || 'Yoga & Wellness Course';
  const currentCourseId = courseData?.id || detectedRouteCourse?.id || null;

  // Initialize form and detect country when modal opens
  useEffect(() => {
    if (isOpen) {
      const initialCountryName = selectedCountry?.country_name || 'India';
      const initialCountryCodeIso = selectedCountry?.country_code || 'IN';

      const matchedCountry = countries.find(
        c => c.code === initialCountryCodeIso || c.name.toLowerCase() === initialCountryName.toLowerCase()
      ) || countries.find(c => c.code === 'IN') || countries[0];

      setFormData({
        name: '',
        email: '',
        countryCode: matchedCountry ? matchedCountry.dial : '+91',
        phone: '',
        country: matchedCountry ? matchedCountry.name : 'India',
        countryIso: matchedCountry ? matchedCountry.code : 'IN',
        message: ''
      });
      setIsSuccess(false);
      setIsSubmitting(false);
      setErrorMessage('');
    }
  }, [isOpen, defaultProgram, courseData, selectedCountry]);

  if (!isOpen) return null;

  // When Phone Code changes -> sync Country Dropdown
  const handleCountryCodeChange = (code) => {
    const matchedCountry = countries.find(c => c.dial === code);
    setFormData(prev => ({
      ...prev,
      countryCode: code,
      country: matchedCountry ? matchedCountry.name : prev.country,
      countryIso: matchedCountry ? matchedCountry.code : prev.countryIso
    }));
  };

  // When Country Dropdown changes -> sync Phone Code automatically while preserving mobile number
  const handleCountrySelectChange = (e) => {
    const countryName = e.target.value;
    const matchedCountry = countries.find(c => c.name === countryName);
    setFormData(prev => ({
      ...prev,
      country: countryName,
      countryCode: matchedCountry ? matchedCountry.dial : prev.countryCode,
      countryIso: matchedCountry ? matchedCountry.code : prev.countryIso
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');

    if (!formData.name.trim()) {
      setErrorMessage('Please enter your full name.');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim() || !emailRegex.test(formData.email.trim())) {
      setErrorMessage('Please enter a valid email address.');
      return;
    }

    if (!formData.phone.trim()) {
      setErrorMessage('Please enter your mobile number.');
      return;
    }

    if (!formData.country) {
      setErrorMessage('Please select your country.');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: formData.name.trim(),
        email: formData.email.trim(),
        mobile: formData.phone.trim(),
        country_code: formData.countryCode,
        mobile_number: formData.phone.trim(),
        country_iso_code: formData.countryIso,
        country: formData.country,
        course_id: currentCourseId,
        message: formData.message.trim() || `Enquiry for ${currentCourseTitle}`,
        source_page: location.pathname
      };

      await apiPost('/save-interested-lead.php', payload);
      setIsSuccess(true);

      // Trigger Google Tag Manager enquiry conversion event
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'pranaflow_enquiry_success'
      });
    } catch (err) {
      console.error('Enquiry submission error:', err);
      setErrorMessage(err.message || 'Failed to send enquiry. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target.id === 'leadModalOverlay') {
      closeModal();
    }
  };

  return (
    <div
      id="leadModalOverlay"
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(15, 30, 24, 0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 999999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        boxSizing: 'border-box'
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '16px',
          boxShadow: '0 20px 40px rgba(10, 35, 25, 0.22)',
          width: '100%',
          maxWidth: '500px',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          boxSizing: 'border-box',
          animation: 'pfModalFadeIn 0.22s ease-out'
        }}
      >
        <style>{`
          @keyframes pfModalFadeIn {
            from { opacity: 0; transform: scale(0.96); }
            to { opacity: 1; transform: scale(1); }
          }
          .pf-modal-close-btn:hover {
            background-color: #e8f0ec !important;
            color: #1f332c !important;
          }
          .pf-form-field {
            margin-bottom: 16px;
          }
          .pf-form-label {
            display: block;
            font-size: 0.85rem;
            font-weight: 700;
            color: #1f332c;
            margin-bottom: 6px;
          }
          .pf-form-input-styled {
            width: 100%;
            padding: 10px 14px;
            font-size: 0.9rem;
            border: 1px solid #d7e2dc;
            border-radius: 8px;
            background-color: #f8fbf9;
            color: #1f332c;
            outline: none;
            box-sizing: border-box;
            transition: border-color 0.15s ease, background-color 0.15s ease;
          }
          .pf-form-input-styled:focus {
            border-color: #1a5c33;
            background-color: #ffffff;
          }
          .pf-course-badge-box {
            background: linear-gradient(135deg, #eaf4ee 0%, #f0f7f2 100%);
            border: 1px solid #cce3d5;
            border-radius: 10px;
            padding: 10px 14px;
            font-weight: 700;
            color: #1a5c33;
            font-size: 0.92rem;
            display: flex;
            align-items: center;
          }
          .pf-submit-btn {
            width: 100%;
            background: linear-gradient(135deg, #1a5c33 0%, #257041 100%);
            color: #ffffff;
            font-weight: 800;
            font-size: 0.95rem;
            padding: 13px 20px;
            border: none;
            border-radius: 30px;
            cursor: pointer;
            transition: all 0.2s ease;
            box-shadow: 0 4px 12px rgba(26, 92, 51, 0.25);
          }
          .pf-submit-btn:hover:not(:disabled) {
            background: linear-gradient(135deg, #144928 0%, #1c5c34 100%);
            box-shadow: 0 6px 16px rgba(26, 92, 51, 0.35);
            transform: translateY(-1px);
          }
          .pf-submit-btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
          }
        `}</style>

        {/* Fixed / Sticky Header */}
        <div
          style={{
            padding: '24px 24px 16px 24px',
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #eef3f0',
            position: 'relative',
            flexShrink: 0,
            zIndex: 2
          }}
        >
          {/* Close Button */}
          <button
            className="pf-modal-close-btn"
            type="button"
            onClick={closeModal}
            aria-label="Close"
            style={{
              position: 'absolute',
              top: '20px',
              right: '20px',
              width: '32px',
              height: '32px',
              borderRadius: '50%',
              border: 'none',
              backgroundColor: '#f0f5f2',
              color: '#556b62',
              fontSize: '18px',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              zIndex: 3
            }}
          >
            &times;
          </button>

          <h3 style={{ margin: '0 0 6px 0', fontSize: '1.35rem', fontWeight: 800, color: '#1f332c', fontFamily: 'var(--heading-font-family)', paddingRight: '36px' }}>
            Enquire About This Course
          </h3>
          <p style={{ margin: 0, fontSize: '0.88rem', color: '#60756c', lineHeight: 1.5, paddingRight: '20px' }}>
            Share your details and our course advisor will contact you.
          </p>
        </div>

        {/* Scrollable Form Content */}
        <div
          style={{
            padding: '20px 24px 28px 24px',
            overflowY: 'auto',
            flex: 1,
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {!isSuccess ? (
            <>
              {errorMessage && (
                <div style={{ backgroundColor: '#fdf2f2', border: '1px solid #f8b4b4', color: '#9b1c1c', padding: '10px 14px', borderRadius: '8px', fontSize: '0.85rem', marginBottom: '16px' }}>
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                {/* 1. Course (Auto Selected, Readonly, No Icon) */}
                <div className="pf-form-field">
                  <label className="pf-form-label">Course</label>
                  <div className="pf-course-badge-box">
                    <span>{currentCourseTitle}</span>
                  </div>
                </div>

                {/* 2. Full Name */}
                <div className="pf-form-field">
                  <label className="pf-form-label" htmlFor="enquiryFullName">Full Name *</label>
                  <input
                    id="enquiryFullName"
                    type="text"
                    className="pf-form-input-styled"
                    placeholder="Full Name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* 3. Email Address */}
                <div className="pf-form-field">
                  <label className="pf-form-label" htmlFor="enquiryEmail">Email Address *</label>
                  <input
                    id="enquiryEmail"
                    type="email"
                    className="pf-form-input-styled"
                    placeholder="Email Address"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* 4. Mobile Number with Country Code */}
                <div className="pf-form-field">
                  <label className="pf-form-label">Mobile Number *</label>
                  <CountryPhoneInput
                    countryCode={formData.countryCode}
                    onCountryCodeChange={handleCountryCodeChange}
                    setCountryCode={handleCountryCodeChange}
                    mobileNumber={formData.phone}
                    onMobileNumberChange={(num) => setFormData({ ...formData, phone: num })}
                    phone={formData.phone}
                    setPhone={(num) => setFormData({ ...formData, phone: num })}
                    disabled={isSubmitting}
                    placeholder="Mobile Number"
                  />
                </div>

                {/* 5. Country Dropdown (Editable, Synced) */}
                <div className="pf-form-field">
                  <label className="pf-form-label" htmlFor="enquiryCountry">Country *</label>
                  <select
                    id="enquiryCountry"
                    className="pf-form-input-styled"
                    value={formData.country}
                    onChange={handleCountrySelectChange}
                    required
                    disabled={isSubmitting}
                  >
                    {countries.map((c) => (
                      <option key={`${c.code}-${c.name}`} value={c.name}>
                        {c.flag} {c.name} ({c.dial})
                      </option>
                    ))}
                  </select>
                </div>

                {/* 6. Message (Optional Textarea) */}
                <div className="pf-form-field">
                  <label className="pf-form-label" htmlFor="enquiryMessage">Message</label>
                  <textarea
                    id="enquiryMessage"
                    className="pf-form-input-styled"
                    rows="3"
                    placeholder="Tell us what you would like to know about this course"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    disabled={isSubmitting}
                    style={{ resize: 'vertical', minHeight: '75px' }}
                  />
                </div>

                {/* 7. Send Enquiry Button */}
                <div style={{ marginTop: '20px' }}>
                  <button
                    type="submit"
                    className="pf-submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending Enquiry...' : 'Send Enquiry'}
                  </button>
                </div>
              </form>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px 10px' }}>
              <div
                style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '50%',
                  backgroundColor: '#e6f7ed',
                  color: '#1a5c33',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 16px auto',
                  fontSize: '28px'
                }}
              >
                ✓
              </div>
              <h3 style={{ margin: '0 0 10px 0', fontSize: '1.35rem', fontWeight: 800, color: '#1f332c' }}>
                Thank You!
              </h3>
              <p style={{ margin: 0, fontSize: '0.92rem', color: '#4a5d55', lineHeight: 1.6 }}>
                Thank you! Your enquiry has been received. Our course advisor will contact you shortly.
              </p>
              <button
                type="button"
                className="pf-submit-btn"
                onClick={closeModal}
                style={{ marginTop: '24px' }}
              >
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LeadModal;
